// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// Reported bugs: the list on the left, the picked report on the right. Owner's
// decision, 2026-09-14 (docs/plans/DECISIONS.md D37): the page had been a
// seven-column table whose rows expanded in place, with a second copy of a
// linked report stacked above it, and it read as cluttered. Open / Fixed / All,
// Only mine and a search sit above the list.
//
// What a reporter sees: the report, its status, and the comments on its ClickUp
// ticket that a developer started with [client-facing] — the server keeps every
// other one back. No task link, no raw ClickUp status, nothing posted back.
//
// `?report=<id>` IS the selection. A ClickUp ticket links here that way (the
// screenshot is linked, not attached, because ClickUp's storage is full), and a
// click in the list writes the same parameter, so the address always names the
// report on screen. With no parameter, the first report in the list is shown.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import {
  getBugReport,
  listBugReports,
  markReportsSeen,
  type BugReportCounts,
  type BugReportSummary,
  type BugReportView,
} from '../../lib/bugReportApi';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import Pagination from '../../components/ui/Pagination';
import SearchInput from '../../components/ui/SearchInput';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { EmptyState, ErrorState, LoadingIndicator } from '../../components/ui/States';
import { BugReportList } from './BugReportList';
import { BugReportDetail } from './BugReportDetail';
import type { CommentsResult } from './useTeamNotes';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

// Reported, Investigating and Being fixed are one "Open" list. Won't fix is
// under All only — no live ClickUp status reaches it (docs/BUG_REPORTER.md).
const VIEWS: Array<{ value: BugReportView; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'all', label: 'All' },
];

export const BugReportsPage: React.FC = () => {
  const [reports, setReports] = useState<BugReportSummary[]>([]);
  const [counts, setCounts] = useState<BugReportCounts | null>(null);
  const [total, setTotal] = useState(0);

  const [view, setView] = useState<BugReportView>('open');
  const [mine, setMine] = useState(false);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Whether the list has answered at least once — until it has, nobody knows
  // whether a linked report is on it.
  const [listSettled, setListSettled] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get('report');
  const [linked, setLinked] = useState<BugReportSummary | null>(null);
  const [linkedError, setLinkedError] = useState<string | null>(null);

  // Comments are read live from ClickUp. Kept for the visit so clicking between
  // two reports does not ask again; Refresh empties it.
  const commentsCache = useRef(new Map<string, CommentsResult>());
  const [refreshKey, setRefreshKey] = useState(0);

  // Sent once typing pauses, and always from page 1: page 3 of every report is
  // rarely a page of the ones that match.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listBugReports({ page, limit: PAGE_SIZE, view, mine, q: query });
      setReports(res.data.reports);
      setCounts(res.data.counts);
      setTotal(res.data.pagination.total);
    } catch (err: any) {
      setError(err?.message || 'The reports could not be loaded.');
    } finally {
      setLoading(false);
      setListSettled(true);
    }
  }, [page, view, mine, query]);

  useEffect(() => {
    load();
  }, [load]);

  // Opening this screen IS reading the updates, so the launcher's dot goes out.
  // Reported separately from the list's own error: failing to clear the dot is
  // not a failure to show the reports, and must not be presented as one.
  useEffect(() => {
    markReportsSeen().catch((err) =>
      // eslint-disable-next-line no-console
      console.warn('[bugReporter] could not mark reports as seen:', err)
    );
  }, []);

  const inList = linkedId ? reports.find((r) => r.id === linkedId) ?? null : null;
  const linkedInList = inList !== null;

  // A linked report can be on another page of the list, or filtered out of it,
  // so it is fetched on its own — through the same visibility rule as the list.
  // Only once the list has answered: most links name a report on its first page,
  // and asking for it before knowing that is a request for nothing.
  useEffect(() => {
    if (!linkedId || !listSettled || linkedInList) return undefined;
    let cancelled = false;
    setLinkedError(null);
    getBugReport(linkedId)
      .then((res) => {
        if (!cancelled) setLinked(res.data.report);
      })
      .catch((err) => {
        if (!cancelled) setLinkedError(err?.message || 'That report could not be opened.');
      });
    return () => {
      cancelled = true;
    };
  }, [linkedId, listSettled, linkedInList]);

  const selected: BugReportSummary | null = linkedId
    ? inList ?? (linked && linked.id === linkedId ? linked : null)
    : reports[0] ?? null;

  // BR-1: a comment written, edited or deleted answers with the whole report, and
  // that answer replaces the copy on screen — in the list, or the linked one.
  const replaceReport = useCallback((next: BugReportSummary) => {
    setReports((rows) => rows.map((r) => (r.id === next.id ? next : r)));
    setLinked((current) => (current && current.id === next.id ? next : current));
  }, []);

  // D47: a link or unlink can move other reports too — an original's instances go
  // with it, and the original's "Seen N times" changes — so the list is asked again.
  const linkChanged = useCallback(
    (next: BugReportSummary) => {
      replaceReport(next);
      load();
    },
    [replaceReport, load]
  );

  const select = (id: string) => setSearchParams({ report: id }, { replace: true });
  const backToList = () => setSearchParams({}, { replace: true });

  const changeView = (next: BugReportView) => {
    setView(next);
    setPage(1);
  };

  const refresh = () => {
    commentsCache.current.clear();
    setRefreshKey((n) => n + 1);
    load();
  };

  const nothingReported = !query && !mine && counts?.all === 0;
  // Below the lg breakpoint the list and the report take turns; a chosen report
  // (a click, or a ticket's link) is what switches to it.
  const reportFirst = Boolean(linkedId);

  return (
    <div className="flex h-full flex-col bg-brand-bg">
      <PageHeader
        title="Reported bugs"
        subtitle="Bugs reported from inside USC: where each one stands, and the development team's comments on it."
        actions={
          <Button onClick={refresh} loading={loading} icon={<RefreshCw aria-hidden="true" className="h-4 w-4" />}>
            Refresh
          </Button>
        }
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <SegmentedControl
            aria-label="Status"
            value={view}
            onChange={changeView}
            segments={VIEWS.map((v) => ({
              value: v.value,
              label: v.label,
              count: counts ? counts[v.value] : undefined,
            }))}
          />
          <label className="flex h-9 select-none items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => {
                setMine(e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 rounded border-slate-300"
            />
            Only mine
          </label>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search title or ticket number"
            className="w-full sm:ml-auto sm:w-72"
          />
        </div>
      </PageHeader>

      <div className="min-h-0 flex-1 px-4 py-4 sm:px-8">
        <div className="flex h-full min-h-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div
            className={`${
              reportFirst ? 'hidden lg:flex' : 'flex'
            } min-h-0 w-full flex-col lg:w-[380px] lg:shrink-0 lg:border-r lg:border-slate-200`}
          >
            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading && reports.length === 0 ? (
                <div className="p-4">
                  <LoadingIndicator label="Loading reports…" />
                </div>
              ) : error ? (
                <div className="p-4">
                  <ErrorState message={error} onRetry={load} />
                </div>
              ) : reports.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    title={nothingReported ? 'No bugs reported yet' : 'No reports match'}
                    detail={
                      nothingReported
                        ? 'Use the bug button at the bottom-left of any screen to report one.'
                        : 'Try All, clear the search, or untick Only mine.'
                    }
                  />
                </div>
              ) : (
                <BugReportList reports={reports} activeId={selected?.id ?? null} onSelect={select} />
              )}
            </div>
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} noun="reports" />
          </div>

          <div
            className={`${
              reportFirst ? 'flex' : 'hidden lg:flex'
            } min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-slate-50`}
          >
            {selected ? (
              <BugReportDetail
                key={selected.id}
                report={selected}
                commentsCache={commentsCache.current}
                refreshKey={refreshKey}
                onBack={backToList}
                onReportChanged={replaceReport}
                onLinkChanged={linkChanged}
                onOpen={select}
              />
            ) : linkedId ? (
              <div className="space-y-3 p-6">
                <div className="lg:hidden">
                  <Button variant="ghost" size="sm" onClick={backToList}>
                    All reports
                  </Button>
                </div>
                {linkedError ? <ErrorState message={linkedError} /> : <LoadingIndicator label="Opening report…" />}
              </div>
            ) : (
              <p className="m-auto p-6 text-sm text-slate-500">Pick a report to read it here.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
